import { liveQuery } from "dexie";
import { useEffect, useRef, useState } from "react";
import { onFnKey, type KeyMap } from "../lib/shortcuts.js";

/** Route F1–F12 to screen actions; ignored while a dialog is open so keys never act behind a modal. */
export function useFnKeys<A extends string>(map: KeyMap<A> | undefined, onKey: ((a: A) => void) | undefined): void {
  const handler = useRef(onKey);
  useEffect(() => { handler.current = onKey; });
  useEffect(() => {
    if (!map) return;
    const h = (e: KeyboardEvent) => {
      const action = onFnKey(map, e, Boolean(document.querySelector(".tos-overlay")));
      if (action) handler.current?.(action);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [map]);
}

/** Re-run an IndexedDB query whenever the tables it reads change (Dexie liveQuery). `key` identifies the query. */
export function useLive<T>(query: () => Promise<T>, key: string, initial: T): T {
  const [value, setValue] = useState<T>(initial);
  const q = useRef(query);
  useEffect(() => { q.current = query; });
  useEffect(() => {
    const sub = liveQuery(() => q.current()).subscribe({
      next: setValue,
      error: (e: unknown) => console.error("Local store query failed", e),
    });
    return () => sub.unsubscribe();
  }, [key]);
  return value;
}

/** Re-render every `ms` (clocks, "x minutes ago" and throughput). */
export function useNow(ms = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(iv);
  }, [ms]);
  return now;
}
